import { ArrowRightIcon, TrashSimpleIcon, XCircleIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { match, P } from 'ts-pattern'
import PlayButton from '../../assets/play-button.svg?react'
import { ScanStatusBadge } from '../../components/badges/scan-status-badge.tsx'
import { GhostButton } from '../../components/buttons/ghost-button.tsx'
import { CancelAnalysisDialog } from '../../components/cancel-analysis-dialog.tsx'
import { CompositionBarWithLabel } from '../../components/composition-bar/composition-bar-with-label'
import { DeleteScanDialog } from '../../components/delete-scan-dialog.tsx'
import { RepositoryName } from '../../components/repository-header.tsx'
import { ScanDateTimeTooltip, toScanDateTimeTooltipProps } from '../../components/scan-details-header.tsx'
import { PaginationControls } from '../../components/table/pagination-controls.tsx'
import { BranchTag } from '../../components/tags/branch-tag'
import { SCMIcon } from '../../components/third-party-branding/scm-icon.tsx'
import { ToolIcon } from '../../components/third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  useGetScanFindings,
  useGetScans,
  useGetScansWithRepositories,
  usePostAnalyzeScan,
} from '../../utils/api-client/api-hooks.ts'
import { type Repository, type ScanResponse, type Tool } from '../../utils/api-client/api-schemas.ts'
import { isScaTool } from '../../utils/detector-utils'
import { getScaProcessedTotal } from '../../utils/finding-statistics.ts'
import * as overviewStyles from '../scans-overview-page.css.ts'
import {
  DEFAULT_SCAN_QUICK_VIEW_ID,
  getScanQuickViewConfig,
  SCAN_QUICK_VIEW_IDS,
  type ScanQuickViewId,
} from './scan-quick-view-config'
import * as compactStyles from './scans-tab.css.ts'

export type ScanListVariant = 'compact' | 'overview'

export const parseScanView = (raw: string | null): ScanQuickViewId =>
  SCAN_QUICK_VIEW_IDS.includes(raw as ScanQuickViewId) ? (raw as ScanQuickViewId) : DEFAULT_SCAN_QUICK_VIEW_ID

export const patchSearchParams = (prev: URLSearchParams, updates: Record<string, string | string[] | null>) => {
  const next = new URLSearchParams(prev)
  for (const [key, value] of Object.entries(updates)) {
    next.delete(key)
    if (value !== null) {
      for (const v of Array.isArray(value) ? value : [value]) {
        next.append(key, v)
      }
    }
  }
  return next
}

const parsePagination = (searchParams: URLSearchParams, defaultPageSize: number) => ({
  pageIndex: Number(searchParams.get('page-number')) || 0,
  pageSize: Number(searchParams.get('page-size')) || defaultPageSize,
})

const usePaginationChange = (setSearchParams: ReturnType<typeof useSearchParams>[1], defaultPageSize: number) =>
  useMemo(
    () =>
      ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => {
        setSearchParams(prev =>
          patchSearchParams(prev, {
            'page-number': pageIndex > 0 ? String(pageIndex) : null,
            'page-size': pageSize !== defaultPageSize ? String(pageSize) : null,
          })
        )
      },
    [setSearchParams, defaultPageSize]
  )

// #region Wrapper

export type ScansListProps = {
  repositoryIds?: string[]
  tools?: Tool[]
  showRepositoryInfo?: boolean
  variant?: ScanListVariant
  repository?: Repository
}

export const ScansList: React.FC<ScansListProps> = props => {
  return <ScansListPure {...useScansListProps(props)} />
}

// #endregion

// #region Hooks

const DEFAULT_PAGE_SIZE = 10

const useScansListProps = ({
  repositoryIds,
  tools,
  showRepositoryInfo,
  variant,
}: ScansListProps): ScansListPureProps => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedScanQuickView = parseScanView(searchParams.get('scan-view'))
  const pagination = parsePagination(searchParams, DEFAULT_PAGE_SIZE)
  const onPaginationChange = usePaginationChange(setSearchParams, DEFAULT_PAGE_SIZE)
  const scanQuickViewCounts = useScanQuickViewCounts({ repositoryIds, tools })
  const { scans, repositories, isScansLoading, totalScans } = useScansForQuickView({
    repositoryIds,
    tools,
    showRepositoryInfo,
  })
  const { onAnalyzeScan } = useAnalyzeScanAction()
  const rowCount = scanQuickViewCounts[selectedScanQuickView] ?? totalScans ?? 0
  const [scanIdToDelete, setScanIdToDelete] = useState<string>()
  const [scanIdToCancel, setScanIdToCancel] = useState<string>()

  return {
    scans,
    isScansLoading,
    selectedScanQuickView,
    onAnalyzeScan,
    onRequestDeleteScan: setScanIdToDelete,
    scanIdToDelete,
    onCloseDeleteScan: () => setScanIdToDelete(undefined),
    onRequestCancelScan: setScanIdToCancel,
    scanIdToCancel,
    onCloseCancelScan: () => setScanIdToCancel(undefined),
    rowCount,
    pagination,
    onPaginationChange,
    repositories,
    showRepositoryInfo,
    variant,
  }
}

type ScanQuickViewCounts = Record<ScanQuickViewId, number | undefined>

export const useScanQuickViewCounts = ({
  repositoryIds,
  tools,
}: { repositoryIds?: string[]; tools?: Tool[] } = {}): ScanQuickViewCounts => {
  const commonParams = {
    page: { number: 0, size: 1 } as const,
    repositoryIds,
    tools,
  }

  const { data: completedScans } = useGetScans({ ...commonParams, analysisState: 'completed' })
  const { data: inProgressScans } = useGetScans({ ...commonParams, analysisState: 'in-progress' })
  const { data: unanalyzedScans } = useGetScans({ ...commonParams, analysisState: 'not-analyzed' })

  return useMemo(
    () => ({
      'scan-completed': completedScans?.total,
      'scan-in-progress': inProgressScans?.total,
      'scan-unanalyzed': unanalyzedScans?.total,
    }),
    [completedScans, inProgressScans, unanalyzedScans]
  )
}

const useScansForQuickView = ({
  repositoryIds,
  tools,
  showRepositoryInfo,
}: {
  repositoryIds?: string[]
  tools?: Tool[]
  showRepositoryInfo?: boolean
}) => {
  const [searchParams] = useSearchParams()
  const pagination = parsePagination(searchParams, DEFAULT_PAGE_SIZE)
  const selectedScanQuickView = parseScanView(searchParams.get('scan-view'))
  const quickViewConfig = getScanQuickViewConfig(selectedScanQuickView)

  const {
    baseResult: { data: paginatedScans, isLoading: isScansLoading },
    linkedResults,
  } = useGetScansWithRepositories({
    page: { number: pagination.pageIndex, size: pagination.pageSize },
    repositoryIds,
    analysisState: quickViewConfig.analysisState,
    tools,
  })

  return useMemo(
    () => ({
      scans: paginatedScans?._embedded.items ?? [],
      repositories: showRepositoryInfo ? linkedResults[0].map(r => r.data).filter(r => !!r) : undefined,
      isScansLoading,
      totalScans: paginatedScans?.total,
    }),
    [paginatedScans, linkedResults, isScansLoading, showRepositoryInfo]
  )
}

const useAnalyzeScanAction = () => {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Analysis trigger failed' })

  const analyzeScanMutation = usePostAnalyzeScan({
    onError: showMutationError,
    onSuccess: () => {
      addToast({
        message: <>Analysis triggered successfully</>,
        variant: 'success',
      })
    },
  })

  return useMemo(
    () => ({ onAnalyzeScan: (scanId: string) => analyzeScanMutation.mutate({ scanId }) }),
    [analyzeScanMutation]
  )
}

// #endregion

// #region Pure Component

export type ScansListPureProps = {
  scans: ScanResponse[]
  isScansLoading: boolean
  selectedScanQuickView: ScanQuickViewId
  onAnalyzeScan: (scanId: string) => unknown
  onRequestDeleteScan?: (scanId: string) => unknown
  scanIdToDelete?: string
  onCloseDeleteScan?: () => unknown
  onRequestCancelScan?: (scanId: string) => unknown
  scanIdToCancel?: string
  onCloseCancelScan?: () => unknown
  rowCount: number
  pagination: { pageIndex: number; pageSize: number }
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void
  repositories?: Repository[]
  showRepositoryInfo?: boolean
  variant?: ScanListVariant
}

export const ScansListPure: React.FC<ScansListPureProps> = ({
  scans,
  isScansLoading,
  selectedScanQuickView,
  onAnalyzeScan,
  onRequestDeleteScan,
  scanIdToDelete,
  onCloseDeleteScan,
  onRequestCancelScan,
  scanIdToCancel,
  onCloseCancelScan,
  rowCount,
  pagination,
  onPaginationChange,
  repositories,
  showRepositoryInfo = false,
  variant = 'compact',
}) => {
  const isEmpty = !isScansLoading && scans.length === 0
  return (
    <>
      <section style={{ margin: '24px 0' }} aria-label="Scans list">
        {isScansLoading ? (
          <ScanListLoadingSkeleton pageSize={pagination.pageSize} variant={variant} />
        ) : isEmpty ? (
          <ScanListEmptyState selectedScanQuickView={selectedScanQuickView} />
        ) : (
          <>
            {scans.map((scan, index) => {
              const repository = showRepositoryInfo ? repositories?.[index] : undefined
              return match(selectedScanQuickView)
                .with('scan-completed', () => (
                  <CompletedScanRow
                    key={scan.id}
                    scan={scan}
                    repository={repository}
                    variant={variant}
                    onRequestDeleteScan={onRequestDeleteScan}
                  />
                ))
                .with('scan-in-progress', () => (
                  <InProgressScanRow
                    key={scan.id}
                    scan={scan}
                    repository={repository}
                    variant={variant}
                    onRequestCancelScan={onRequestCancelScan}
                  />
                ))
                .with('scan-unanalyzed', () => (
                  <UnanalyzedScanRow
                    key={scan.id}
                    scan={scan}
                    onAnalyzeScan={onAnalyzeScan}
                    onRequestDeleteScan={onRequestDeleteScan}
                    repository={repository}
                    variant={variant}
                  />
                ))
                .exhaustive()
            })}
          </>
        )}
      </section>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PaginationControls
          tableName="Scan & analyses"
          totalItems={rowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onPageIndexChange={pageIndex => onPaginationChange({ pageIndex, pageSize: pagination.pageSize })}
          onPageSizeChange={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
        />
      </div>
      {scanIdToDelete && onCloseDeleteScan && (
        <DeleteScanDialog scanId={scanIdToDelete} onClose={onCloseDeleteScan} onDeleteSuccess={onCloseDeleteScan} />
      )}
      {scanIdToCancel && onCloseCancelScan && (
        <CancelAnalysisDialog scanId={scanIdToCancel} onClose={onCloseCancelScan} onCancelSuccess={onCloseCancelScan} />
      )}
    </>
  )
}

// #endregion

// #region Sub-components

type CompletedScanRowProps = {
  scan: ScanResponse
  repository?: Repository
  variant?: ScanListVariant
  onRequestDeleteScan?: (scanId: string) => unknown
}

export const CompletedScanRow: React.FC<CompletedScanRowProps> = ({
  scan,
  repository,
  variant = 'compact',
  onRequestDeleteScan,
}) => {
  const isSca = isScaTool(scan.detector)
  const type = isSca ? 'sca' : 'triage'
  const compositionBarSize = variant === 'overview' ? 'small' : 'tiny'

  const { data: scanFindings, isLoading: isFindingsLoading } = useGetScanFindings({
    scanId: scan.id,
  })

  const compositionData = useMemo(() => {
    if (!scanFindings) return undefined
    const total = isSca
      ? getScaProcessedTotal(scanFindings)
      : scanFindings.completed_analysis + scanFindings.in_progress_analysis
    return { statistics: scanFindings, total }
  }, [scanFindings, isSca])

  const compositionBar = (
    <div style={{ flex: 1 }}>
      {match({ isFindingsLoading, compositionData, type })
        .with(P.union({ isFindingsLoading: true }, { compositionData: undefined }), () => (
          <CompositionBarWithLabel type="loading" size={compositionBarSize} />
        ))
        .with({ compositionData: P.not(undefined), type: 'sca' }, ({ compositionData }) => (
          <CompositionBarWithLabel
            type="sca"
            statistics={compositionData.statistics.sca}
            total={compositionData.total}
            tool={scan.detector}
            size={compositionBarSize}
          />
        ))
        .with({ compositionData: P.not(undefined) }, ({ compositionData }) => (
          <CompositionBarWithLabel
            type="triage"
            statistics={compositionData.statistics.triage}
            total={compositionData.total}
            tool={scan.detector}
            size={compositionBarSize}
          />
        ))
        .exhaustive()}
    </div>
  )

  const statusArea =
    variant === 'overview' ? (
      <ScanStatusBadge variant="completed" />
    ) : (
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {onRequestDeleteScan && (
          <TooltipWithHover
            trigger={
              <GhostButton
                type="destructive"
                size="extra-small"
                ariaLabel="Delete scan"
                className={compactStyles.deleteButton}
                data-testid="delete-scan-button"
                onClick={e => {
                  e.stopPropagation()
                  onRequestDeleteScan(scan.id)
                }}
              >
                <TrashSimpleIcon size={20} weight="light" />
              </GhostButton>
            }
            size="small"
          >
            Delete scan
          </TooltipWithHover>
        )}
        <div className={compactStyles.badgeWithHoverLink}>
          <ScanStatusBadge variant="completed" />
          <Link to={Routes.ScanDetailsPage.createPath({ scanId: scan.id })} className={compactStyles.hoverAnalysisLink}>
            View details
            <ArrowRightIcon size={16} weight="light" />
          </Link>
        </div>
      </div>
    )

  return (
    <ScanRowLayout
      scan={scan}
      testId={`scan-${scan.id}`}
      repository={repository}
      variant={variant}
      compositionBar={compositionBar}
      statusArea={statusArea}
    />
  )
}

type InProgressScanRowProps = {
  scan: ScanResponse
  repository?: Repository
  variant?: ScanListVariant
  onRequestCancelScan?: (scanId: string) => unknown
}

export const InProgressScanRow: React.FC<InProgressScanRowProps> = ({
  scan,
  repository,
  variant = 'compact',
  onRequestCancelScan,
}) => {
  const compositionBarSize = variant === 'overview' ? 'small' : 'tiny'

  const compositionBar = (
    <div style={{ flex: 1 }}>
      <CompositionBarWithLabel type="in-progress" size={compositionBarSize} />
    </div>
  )

  const statusArea =
    variant === 'overview' ? (
      <ScanStatusBadge variant="in-progress" />
    ) : (
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {onRequestCancelScan && (
          <TooltipWithHover
            trigger={
              <GhostButton
                type="destructive"
                size="extra-small"
                ariaLabel="Cancel analysis"
                className={compactStyles.deleteButton}
                data-testid="cancel-analysis-button"
                onClick={e => {
                  e.stopPropagation()
                  onRequestCancelScan(scan.id)
                }}
              >
                <XCircleIcon size={20} weight="light" />
              </GhostButton>
            }
            size="small"
          >
            Cancel analysis
          </TooltipWithHover>
        )}
        <div className={compactStyles.badgeWithHoverLink}>
          <ScanStatusBadge variant="in-progress" />
          <Link to={Routes.ScanDetailsPage.createPath({ scanId: scan.id })} className={compactStyles.hoverAnalysisLink}>
            View details
            <ArrowRightIcon size={16} weight="light" />
          </Link>
        </div>
      </div>
    )

  return (
    <ScanRowLayout
      scan={scan}
      testId={`scan-${scan.id}`}
      repository={repository}
      variant={variant}
      compositionBar={compositionBar}
      statusArea={statusArea}
    />
  )
}

type UnanalyzedScanRowProps = {
  scan: ScanResponse
  onAnalyzeScan: (scanId: string) => unknown
  onRequestDeleteScan?: (scanId: string) => unknown
  repository?: Repository
  variant?: ScanListVariant
}

export const UnanalyzedScanRow: React.FC<UnanalyzedScanRowProps> = ({
  scan,
  onAnalyzeScan,
  onRequestDeleteScan,
  repository,
  variant = 'compact',
}) => {
  const compositionBarSize = variant === 'overview' ? 'small' : 'tiny'

  const compositionBar = (
    <div style={{ flex: 1 }}>
      <CompositionBarWithLabel type="loading" ariaLabel="Scan not yet analyzed" size={compositionBarSize} />
    </div>
  )

  const playButton = (
    <button
      aria-label="Trigger analysis"
      onClick={e => {
        if (variant === 'overview') e.preventDefault()
        onAnalyzeScan(scan.id)
      }}
    >
      <PlayButton style={{ color: mappedColors.neutral.halo }} />
    </button>
  )

  const statusArea =
    variant === 'overview' ? (
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-end' }}>
        <ScanStatusBadge variant="unanalyzed" />
        {playButton}
      </div>
    ) : (
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {onRequestDeleteScan && (
          <TooltipWithHover
            trigger={
              <GhostButton
                type="destructive"
                size="extra-small"
                ariaLabel="Delete scan"
                className={compactStyles.deleteButton}
                data-testid="delete-scan-button"
                onClick={e => {
                  e.stopPropagation()
                  onRequestDeleteScan(scan.id)
                }}
              >
                <TrashSimpleIcon size={20} weight="light" />
              </GhostButton>
            }
            size="small"
          >
            Delete scan
          </TooltipWithHover>
        )}
        <div className={compactStyles.badgeWithHoverLink}>
          <ScanStatusBadge variant="unanalyzed" />
          <Link to={Routes.ScanDetailsPage.createPath({ scanId: scan.id })} className={compactStyles.hoverAnalysisLink}>
            View details
            <ArrowRightIcon size={16} weight="light" />
          </Link>
        </div>
        {playButton}
      </div>
    )

  return (
    <ScanRowLayout
      scan={scan}
      testId={`scan-${scan.id}`}
      repository={repository}
      variant={variant}
      compositionBar={compositionBar}
      statusArea={statusArea}
    />
  )
}

type ScanRowLayoutProps = {
  scan: ScanResponse
  testId: string
  repository?: Repository
  variant?: ScanListVariant
  compositionBar: React.ReactNode
  statusArea: React.ReactNode
}

export const ScanRowLayout: React.FC<ScanRowLayoutProps> = ({
  scan,
  testId,
  repository,
  variant = 'compact',
  compositionBar,
  statusArea,
}) => {
  const date = new Date(scan.imported_at)
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  })
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })

  if (variant === 'overview') {
    return (
      <Link
        to={Routes.ScanDetailsPage.createPath({ scanId: scan.id })}
        aria-label={`Scan ${scan.id}`}
        className={overviewStyles.overviewScanArticle}
        data-testid={testId}
      >
        <div className={overviewStyles.overviewScanContainer} data-testid="scan-container">
          <div className={overviewStyles.overviewGridCell1}>
            <ToolIcon tool={scan.detector} size="large+" />
          </div>

          <div className={overviewStyles.overviewGridCell2}>
            {repository && (
              <>
                <SCMIcon type={repository.type} size="medium+" withAriaLabel={true} />
                <RepositoryName displayName={repository.name} isTruncated={false} />
              </>
            )}
          </div>

          <div className={overviewStyles.overviewGridCell3}>{statusArea}</div>

          <div className={overviewStyles.overviewGridCell4}>
            <div className={overviewStyles.overviewDateRow}>
              <p className={overviewStyles.overviewDate}>{formattedDate}</p>
              <ScanDateTimeTooltip {...toScanDateTimeTooltipProps(scan)} />
            </div>
            <p className={overviewStyles.overviewTime}>{formattedTime}</p>
            {scan.branch && <BranchTag branch={scan.branch} />}
          </div>

          <div className={overviewStyles.overviewGridCell5}>{compositionBar}</div>

          <div className={overviewStyles.overviewGridCell6}>
            <span className={overviewStyles.overviewAnalysisLink}>
              View details
              <ArrowRightIcon size={16} weight="light" />
            </span>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <article aria-label={`Scan ${scan.id}`} className={compactStyles.scanArticle} data-testid={testId}>
      <div className={compactStyles.scanContainer} data-testid="scan-container">
        {repository && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', minWidth: '200px' }}>
            <ToolIcon tool={scan.detector} size="large+" />
            <SCMIcon type={repository.type} size="medium+" withAriaLabel={true} />
            <RepositoryName repositoryId={repository.id} displayName={repository.name} isTruncated={false} />
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '200px', minWidth: '200px' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <p className={compactStyles.date}>{formattedDate}</p>
            <p className={compactStyles.time}>{formattedTime}</p>
          </div>
          {scan.branch && <BranchTag branch={scan.branch} />}
        </div>

        {compositionBar}
        {statusArea}
      </div>
    </article>
  )
}

type ScanListLoadingSkeletonProps = {
  pageSize: number
  variant?: ScanListVariant
}

export const ScanListLoadingSkeleton: React.FC<ScanListLoadingSkeletonProps> = ({ pageSize, variant = 'compact' }) => {
  const skeletonRows =
    variant === 'overview'
      ? Array.from({ length: pageSize }, (_, index) => (
          <article key={index} className={overviewStyles.overviewScanArticle} data-testid="scan-container">
            <div className={overviewStyles.overviewScanContainer}>
              <div className={overviewStyles.overviewGridCell1}>
                <div className={overviewStyles.overviewSkeletonToolIcon} />
              </div>
              <div className={overviewStyles.overviewGridCell2}>
                <div className={overviewStyles.overviewSkeletonRepoName} />
              </div>
              <div className={overviewStyles.overviewGridCell3}>
                <div className={overviewStyles.overviewSkeletonBadge} />
              </div>
              <div className={overviewStyles.overviewGridCell4}>
                <div className={overviewStyles.overviewSkeletonDate} />
                <div className={overviewStyles.overviewSkeletonTime} />
                <div className={overviewStyles.overviewSkeletonBranch} />
              </div>
              <div className={overviewStyles.overviewGridCell5}>
                <CompositionBarWithLabel type="loading" size="small" />
              </div>
            </div>
          </article>
        ))
      : Array.from({ length: pageSize }, (_, index) => (
          <article key={index} className={compactStyles.scanArticle} data-testid="scan-container">
            <div className={compactStyles.scanContainer}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '200px', minWidth: '200px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className={compactStyles.skeletonDate} />
                  <div className={compactStyles.skeletonTime} />
                </div>
                <div className={compactStyles.skeletonBranch} />
              </div>

              <div style={{ flex: 1 }}>
                <div className={compactStyles.skeletonCompositionBar} />
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <div className={compactStyles.skeletonStatus} />
                <div className={compactStyles.skeletonButton} />
              </div>
            </div>
          </article>
        ))

  return (
    <>
      <span
        style={{ position: 'absolute', left: '-10000px' }}
        role="status"
        aria-label="Loading scans..."
        aria-live="polite"
      >
        Loading scans...
      </span>
      {skeletonRows}
    </>
  )
}

type ScanListEmptyStateProps = {
  selectedScanQuickView: ScanQuickViewId
}

export const ScanListEmptyState: React.FC<ScanListEmptyStateProps> = ({ selectedScanQuickView }) => {
  const message = match(selectedScanQuickView)
    .with('scan-unanalyzed', () => 'No unanalyzed scans or incomplete analyses found.')
    .with('scan-completed', () => 'No completed analyses found. Run Pixee analysis on scans to see results.')
    .with('scan-in-progress', () => 'No analyses are currently in progress')
    .exhaustive()

  return <p className={compactStyles.noScansMessage}>{message}</p>
}

// #endregion
